// ============================================================
// firmLogo.ts — the firm logo for the PRINTED documents (bill, credit note,
// client statement, case summary), as a data URI so the print tab never
// fetches anything with auth.
//
// A document's letterhead names its logo by an opaque `logoId` (an issued bill
// / credit note → the logo frozen at issue; a draft / statement → today's).
// Ids are immutable (each upload is a new one, never overwritten), so a loaded
// logo is cached for the session. Printing is synchronous (the tab must open
// inside the click), so callers load the logo AHEAD with useFirmLogos /
// useFirmLogo and read it with cachedFirmLogo at print time. A logo that
// can't load simply isn't printed — the document never fails because of it.
// ============================================================
import { useEffect, useState } from 'react'
import { getFirmLogoByIdApi } from '../api/casevoApi'

const cache = new Map<string, string | null>()
const inflight = new Map<string, Promise<string | null>>()

export const cachedFirmLogo = (logoId: string | null | undefined): string | null =>
  (logoId ? cache.get(logoId) ?? null : null)

const loadFirmLogo = (logoId: string): Promise<string | null> => {
  if (cache.has(logoId)) return Promise.resolve(cache.get(logoId) ?? null)
  const running = inflight.get(logoId)
  if (running) return running
  const p = getFirmLogoByIdApi(logoId)
    .then((uri) => uri, () => null)
    .then((uri) => { cache.set(logoId, uri); inflight.delete(logoId); return uri })
  inflight.set(logoId, p)
  return p
}

// Seed the cache with a logo already in hand (e.g. after an upload).
export const primeFirmLogo = (logoId: string, dataUri: string | null): void => { cache.set(logoId, dataUri) }

// Load these logos; re-renders once each arrives. Returns a version number to
// put in a memo's deps so previews rebuild with the logo.
export const useFirmLogos = (logoIds: (string | null | undefined)[]): number => {
  const [version, setVersion] = useState(0)
  const key = Array.from(new Set(logoIds.filter((id): id is string => !!id))).sort().join('|')
  useEffect(() => {
    if (!key) return
    let alive = true
    const missing = key.split('|').filter((id) => !cache.has(id))
    if (missing.length === 0) return
    void Promise.all(missing.map(loadFirmLogo)).then(() => { if (alive) setVersion((v) => v + 1) })
    return () => { alive = false }
  }, [key])
  return version
}

// One logo → its data URI (null while loading / none).
export const useFirmLogo = (logoId: string | null | undefined): string | null => {
  useFirmLogos([logoId])
  return cachedFirmLogo(logoId)
}
